<script setup>
import { i18n } from 'boot/i18n';
import {
  HrConfirmBtn, HrContentCard, HrList, HrListItem, HrSubtleBtn,
} from 'src/design-system';
import { computed } from 'vue';

import { fixForHoldTask } from '../../domain/services/holdTaskFix';
import { useHoldTaskFacts } from '../composables/useHoldTaskFacts';

/**
 * What is wrong, the one action that fixes it, and what it blocks. It opens the editor where the fix
 * is made, through the host's `fixSurfaces`, and only for a user that editor admits.
 *
 * ONE STATEMENT, READ TOP TO BOTTOM: the problem as a complete title, its facts as plain rows, the
 * way to what it blocks, then the fix. Facts rather than how the domain works: an authorization hold
 * lists each payer and code with the services nothing covers. The fix's label already says where it
 * happens, so the card does not name the place again.
 *
 * ONE START EDGE FOR THE TEXT. The fact rows give up their own inset, so they start where the
 * title does.
 */
defineOptions({ name: 'HoldFixCard' });

const props = defineProps({
  /** @type {import('vue').PropType<import('../../domain/model/HoldTask.types').HoldTask>} */
  hold: {
    type: Object,
    required: true,
  },
  /** @type {import('vue').PropType<import('../../application/ports/FixSurfaces.port').FixSurfaces>} */
  fixSurfaces: {
    type: Object,
    required: true,
  },
  /** Where this task's billing items are listed, from the host that owns the routes. */
  itemsLink: {
    type: [String, Object],
    required: true,
  },
});

const fix = computed(() => fixForHoldTask(props.hold));
const canFix = computed(() => fix.value !== null && props.fixSurfaces.canFix(fix.value));
const typeKey = computed(() => `billingItems.holdTask.${props.hold.holdType}`);

/** Whatever the task type's catalog entry says its facts are; this card never asks which type it has. */
const facts = useHoldTaskFacts(() => props.hold);
</script>

<template>
  <hr-content-card
    v-if="hold.blocking"
    :title="facts.description.value ?? i18n.t(`${typeKey}.description`)"
  >
    <div class="row q-col-gutter-md">
      <!-- A placeholder in the facts' own shape while they are read, so they do not appear from nowhere. -->
      <div
        v-if="facts.reading.value"
        class="col-12"
      >
        <hr-list
          separator
          data-e2e="list-hold-task-facts-pending"
        >
          <hr-list-item
            :caption="facts.withCaption.value ? '' : null"
            pending
            flush
          />
        </hr-list>
      </div>
      <div
        v-else-if="facts.rows.value.length > 0"
        class="col-12"
      >
        <hr-list
          separator
          data-e2e="list-hold-task-facts"
        >
          <hr-list-item
            v-for="row in facts.rows.value"
            :key="row.key"
            :label="row.label"
            :caption="row.caption ?? null"
            :trailing-text="row.trailing ?? null"
            flush
          />
        </hr-list>
      </div>
      <div
        v-if="fix && !canFix"
        class="col-12"
      >
        {{ i18n.t('billingItems.holdTask.textNoAccess') }}
      </div>
      <div class="col-12">
        <hr-subtle-btn
          :label="i18n.tc('billingItems.holdTask.buttonViewBlocked', hold.blockedCount, { count: hold.blockedCount })"
          :to="itemsLink"
          data-e2e="button-hold-task-items"
        />
      </div>
    </div>
    <template
      v-if="canFix"
      #actions
    >
      <hr-confirm-btn
        :label="i18n.t(`${typeKey}.action`)"
        data-e2e="button-hold-task-fix"
        @click="fixSurfaces.openFix(fix)"
      />
    </template>
  </hr-content-card>
</template>
